/**
 * DOB-gebundener Auth-Tag — siehe `docs/cancel-auth-tag-spec.md`.
 *
 * Vertrag (kurz):
 *   hmac_key      = SHA-256(utf8(dob))
 *   hmac_message  = utf8(tenantId + ":" + base64Token)
 *   auth_tag      = base64url-no-padding( HMAC-SHA-256(hmac_key, hmac_message) )
 *
 * Cross-Runtime: Node 22 (`globalThis.crypto.subtle`) und alle modernen Browser.
 */

const HMAC_PARAMS: HmacImportParams = { name: 'HMAC', hash: 'SHA-256' };
const KEY_USAGES: readonly KeyUsage[] = ['sign'];

export interface DobAuthTagInput {
  /** Geburtsdatum als ISO-8601-Date-String (`YYYY-MM-DD`). */
  readonly dob: string;
  /** Tenant-UUID in Standard-Lowercase-Hex. */
  readonly tenantId: string;
  /** Cancel-Token im 24-Zeichen-Base64URL-Format aus dem URL-Pfad. */
  readonly base64Token: string;
}

/**
 * Berechnet den `auth_tag` für eine Cancel-Submission.
 *
 * Validiert Pflicht-Felder defensiv (Frontend ist Boundary). Decodierung
 * der UTF-8-Bytes per `TextEncoder`; Base64URL-Output ohne Padding.
 *
 * @throws Error bei leerem `dob`, `tenantId` oder `base64Token`.
 */
export async function computeDobAuthTag(input: DobAuthTagInput): Promise<string> {
  assertNonEmpty(input.dob, 'dob');
  assertNonEmpty(input.tenantId, 'tenantId');
  assertNonEmpty(input.base64Token, 'base64Token');

  const encoder = new TextEncoder();
  const hmacKey = new Uint8Array(await crypto.subtle.digest('SHA-256', encoder.encode(input.dob)));
  const messageBytes = encoder.encode(`${input.tenantId}:${input.base64Token}`);
  const cryptoKey = await crypto.subtle.importKey('raw', hmacKey, HMAC_PARAMS, false, [
    ...KEY_USAGES,
  ]);
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageBytes);
  return encodeBase64UrlNoPadding(new Uint8Array(signature));
}

function assertNonEmpty(value: unknown, name: string): void {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`computeDobAuthTag: ${name} must be a non-empty string`);
  }
}

const BASE64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const BITS_PER_BYTE = 8;
const BITS_PER_CHAR = 6;
const SEXTET_MASK = 0x3f;

/**
 * Base64URL-Encoder ohne Padding. Lokal gehalten, damit das Crypto-Modul
 * keine Abhängigkeit zum Token-Codec hat.
 */
function encodeBase64UrlNoPadding(bytes: Uint8Array): string {
  let bitBuffer = 0;
  let bitCount = 0;
  let output = '';
  for (const byte of bytes) {
    bitBuffer = (bitBuffer << BITS_PER_BYTE) | byte;
    bitCount += BITS_PER_BYTE;
    while (bitCount >= BITS_PER_CHAR) {
      bitCount -= BITS_PER_CHAR;
      const index = (bitBuffer >> bitCount) & SEXTET_MASK;
      output += BASE64URL_ALPHABET[index];
    }
  }
  if (bitCount > 0) {
    const index = (bitBuffer << (BITS_PER_CHAR - bitCount)) & SEXTET_MASK;
    output += BASE64URL_ALPHABET[index];
  }
  return output;
}
